import { Star, StarBorder, StarHalf } from '@mui/icons-material';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { amazonColors } from '../../../config/amazonTheme';

interface StarRatingProps {
  value: number;
  onChange?: (value: number) => void;
  readonly?: boolean;
  size?: 'small' | 'medium' | 'large';
  showLabel?: boolean;
  precision?: 0.5 | 1;
}

const sizeMap = {
  small: { icon: 20, spacing: 0.25 },
  medium: { icon: 28, spacing: 0.5 },
  large: { icon: 36, spacing: 0.75 },
};

const StarRating: React.FC<StarRatingProps> = ({
  value,
  onChange,
  readonly = false,
  size = 'medium',
  showLabel = false,
  precision = 1,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  const { icon: iconSize, spacing } = sizeMap[size];

  // Responsive icon size: smaller on mobile
  const responsiveIconSize = {
    xs: iconSize * 0.75,
    sm: iconSize,
  };

  const ratingLabels: Record<number, string> = {
    1: t('reviews.ratingLabels.poor'),
    2: t('reviews.ratingLabels.fair'),
    3: t('reviews.ratingLabels.good'),
    4: t('reviews.ratingLabels.veryGood'),
    5: t('reviews.ratingLabels.excellent'),
  };

  const displayValue = hoverValue !== null ? hoverValue : value;
  const starColor = amazonColors.orange;

  const handleMouseEnter = (starValue: number) => {
    if (!readonly) {
      setHoverValue(starValue);
    }
  };

  const handleMouseLeave = () => {
    if (!readonly) {
      setHoverValue(null);
    }
  };

  const handleClick = (starValue: number) => {
    if (!readonly && onChange) {
      onChange(starValue);
    }
  };

  const renderStar = (starIndex: number) => {
    const starValue = starIndex + 1;
    const filled = displayValue >= starValue;
    const halfFilled = precision === 0.5 && displayValue >= starValue - 0.5 && displayValue < starValue;

    const StarIcon = filled ? Star : halfFilled ? StarHalf : StarBorder;

    if (readonly) {
      return (
        <StarIcon
          key={starIndex}
          sx={{
            fontSize: responsiveIconSize,
            color: filled || halfFilled ? starColor : theme.palette.grey[300],
            transition: 'color 0.15s ease',
          }}
        />
      );
    }

    return (
      <IconButton
        key={starIndex}
        size="small"
        onClick={() => handleClick(starValue)}
        onMouseEnter={() => handleMouseEnter(starValue)}
        onMouseLeave={handleMouseLeave}
        sx={{
          p: spacing,
          '&:hover': {
            bgcolor: 'transparent',
          },
        }}
      >
        <StarIcon
          sx={{
            fontSize: responsiveIconSize,
            color: filled || halfFilled ? starColor : theme.palette.grey[300],
            transition: 'all 0.15s ease',
            transform: hoverValue === starValue ? 'scale(1.15)' : 'scale(1)',
          }}
        />
      </IconButton>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          ...(readonly && { gap: spacing / 2 }),
        }}
        onMouseLeave={handleMouseLeave}
      >
        {[0, 1, 2, 3, 4].map(renderStar)}
      </Box>
      {showLabel && displayValue > 0 && (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 500,
            minWidth: { xs: 50, sm: 70 },
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
          }}
        >
          {ratingLabels[Math.round(displayValue)] || ''}
        </Typography>
      )}
    </Box>
  );
};

export default React.memo(StarRating);
